/**
 * Navbar Component
 * Main navigation bar with responsive mobile menu
 */

import { isAuthenticated, storage } from '../utils/storage.js';
import { authApi } from '../services/api.js';

export function renderNavbar(options = {}) {
  const { transparent = false, showAuthButtons = true } = options;
  const user = storage.getUser();
  const isLoggedIn = isAuthenticated();

  return `
    <nav class="navbar ${transparent ? 'navbar-transparent' : ''}">
      <div class="container navbar-content">
        <a href="/" data-link class="navbar-logo">
          <svg width="36" height="36" viewBox="0 0 100 100" fill="none">
            <defs>
              <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#1a5276"/>
                <stop offset="100%" style="stop-color:#0f3049"/>
              </linearGradient>
            </defs>
            <rect width="100" height="100" rx="16" fill="url(#logoGrad)"/>
            <path d="M50 20L20 45V80H40V60H60V80H80V45L50 20Z" fill="white"/>
            <circle cx="50" cy="48" r="7" fill="url(#logoGrad)"/>
          </svg>
          <span>Jóvenes Independientes</span>
        </a>
        
        <div class="navbar-nav" id="navbarNav">
          ${isLoggedIn ? `
            <a href="/dashboard" data-link class="navbar-link">Dashboard</a>
            <a href="/guia" data-link class="navbar-link">Guía</a>
            <a href="/gastos" data-link class="navbar-link">Gastos</a>
            <a href="/ahorro" data-link class="navbar-link">Ahorro</a>
            <a href="/ayudas" data-link class="navbar-link">Ayudas</a>
            <a href="/plantillas" data-link class="navbar-link">Plantillas</a>
          ` : `
            <a href="#caracteristicas" class="navbar-link">Características</a>
            <a href="#como-funciona" class="navbar-link">Cómo funciona</a>
            <a href="#publico" class="navbar-link">Público</a>
          `}
        </div>
        
        <div class="navbar-actions">
          ${isLoggedIn ? `
            <div style="position: relative;">
              <button class="btn btn-ghost btn-icon" id="userMenuBtn" aria-label="Menú de usuario">
                <i data-feather="user"></i>
              </button>
              <div class="user-dropdown hidden" id="userDropdown">
                <div style="padding: 12px 16px; border-bottom: 1px solid var(--gray-100);">
                  <div style="font-weight: 600; color: var(--gray-900);">${user?.email || 'Usuario'}</div>
                  <div style="font-size: 12px; color: var(--gray-500);">Plan: Gratuito</div>
                </div>
                <button class="dropdown-item" id="logoutBtn">
                  <i data-feather="log-out"></i>
                  Cerrar sesión
                </button>
              </div>
            </div>
          ` : showAuthButtons ? `
            <a href="/auth" data-link class="btn btn-ghost">Iniciar sesión</a>
            <a href="/auth" data-link class="btn btn-primary">
              <i data-feather="user-plus"></i>
              Registrarse
            </a>
          ` : ''}
          
          <button class="btn btn-ghost btn-icon navbar-mobile-toggle" id="mobileMenuBtn" aria-label="Menú">
            <i data-feather="menu"></i>
          </button>
        </div>
      </div>
      
      <!-- Mobile Menu -->
      <div class="mobile-menu" id="mobileMenu">
        <div class="mobile-menu-nav">
          ${isLoggedIn ? `
            <a href="/dashboard" data-link class="mobile-menu-link">Dashboard</a>
            <a href="/guia" data-link class="mobile-menu-link">Guía Hipotecaria</a>
            <a href="/gastos" data-link class="mobile-menu-link">Gastos Mensuales</a>
            <a href="/ahorro" data-link class="mobile-menu-link">Plan de Ahorro</a>
            <a href="/ayudas" data-link class="mobile-menu-link">Ayudas Públicas</a>
            <a href="/plantillas" data-link class="mobile-menu-link">Plantillas Excel</a>
            <button class="btn btn-secondary w-full mt-4" id="logoutBtnMobile">
              Cerrar sesión
            </button>
          ` : `
            <a href="/#caracteristicas" class="mobile-menu-link">Características</a>
            <a href="/#como-funciona" class="mobile-menu-link">Cómo funciona</a>
            <a href="/#publico" class="mobile-menu-link">Público</a>
            <a href="/auth" data-link class="btn btn-primary w-full mt-4">
              Empezar gratis
            </a>
          `}
        </div>
      </div>
    </nav>
    
    <style>
      .user-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        margin-top: 8px;
        background: white;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-xl);
        min-width: 200px;
        overflow: hidden;
        z-index: var(--z-dropdown);
      }
      
      .user-dropdown.hidden {
        display: none;
      }
      
      .dropdown-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        width: 100%;
        text-align: left;
        color: var(--gray-700);
        font-size: 14px;
        transition: var(--transition-fast);
        border: none;
        background: none;
        cursor: pointer;
      }
      
      .dropdown-item:hover {
        background: var(--gray-50);
        color: var(--primary-600);
      }
      
      .dropdown-item svg {
        width: 16px;
        height: 16px;
      }
    </style>
  `;
}

// Initialize navbar interactions
document.addEventListener('routeChanged', () => {
  initNavbarInteractions();
});

function initNavbarInteractions() {
  // Mobile menu toggle
  const mobileMenuBtn = document.getElementById('mobileMenuBtn');
  const mobileMenu = document.getElementById('mobileMenu');

  if (mobileMenuBtn && mobileMenu) {
    mobileMenuBtn.addEventListener('click', () => {
      mobileMenu.classList.toggle('active');
    });

    // Close on link click
    mobileMenu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        mobileMenu.classList.remove('active');
      });
    });
  }

  // User dropdown toggle
  const userMenuBtn = document.getElementById('userMenuBtn');
  const userDropdown = document.getElementById('userDropdown');

  if (userMenuBtn && userDropdown) {
    userMenuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      userDropdown.classList.toggle('hidden');
    });

    // Close on outside click
    document.addEventListener('click', () => {
      userDropdown.classList.add('hidden');
    });
  }

  // Logout buttons
  const logoutBtn = document.getElementById('logoutBtn');
  const logoutBtnMobile = document.getElementById('logoutBtnMobile');

  const handleLogout = async () => {
    await authApi.logout();
    storage.clearAll();
    window.router.navigate('/');
  };

  if (logoutBtn) logoutBtn.addEventListener('click', handleLogout);
  if (logoutBtnMobile) logoutBtnMobile.addEventListener('click', handleLogout);

  // Navbar scroll effect
  const navbar = document.querySelector('.navbar');
  if (navbar) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 50) {
        navbar.classList.add('scrolled');
      } else {
        navbar.classList.remove('scrolled');
      }
    });
  }
}

// Run on initial load
setTimeout(initNavbarInteractions, 100);
